/**
 * Agregação do recorte geográfico do Google Analytics.
 *
 * `geography_data`, em `GET /integrations/google-analytics/complete-data`, é a tabela
 * `google_analytics_geography_metrics` crua: **uma linha por (dia, país, região, cidade)**,
 * em ordem de data. Fatiar essa lista com `slice(0, 10)` não produz "os dez países com mais
 * sessões" — produz as dez primeiras linhas do primeiro dia do período, com o mesmo país
 * repetido uma vez por cidade (M-194). É o mesmo defeito que `ga4-technology.ts` corrigiu
 * no card NAVEGADORES.
 *
 * ## A taxa de conversão do país é a média ponderada por sessões
 *
 * Taxa não se soma. `conversion_rate` é o `sessionConversionRate` do GA4 em porcentagem —
 * sessões com conversão sobre sessões —, e uma sessão pertence a uma única cidade. Então
 * `Σ(taxa × sessões) / Σ sessões` reconstrói as sessões convertidas do país sobre as sessões
 * do país. A única distância para a taxa do GA4 é o arredondamento de cada linha, que o
 * banco grava com duas casas (`Decimal(5,2)`) — e não a de uma média de taxas.
 *
 * Linha sem taxa não entra no denominador: contar as sessões dela puxaria a média para
 * baixo com uma conversão que ninguém mediu. País sem nenhuma sessão com taxa sai `null`.
 *
 * O GA4 publica `(not set)` para o tráfego sem país atribuído; ele cai na mesma categoria do
 * país ausente, em vez de aparecer como um segundo "desconhecido" no card.
 */
export interface LinhaDeGeografia {
  readonly country?: string | null;
  readonly sessions?: number | null;
  readonly revenue?: number | null;
  readonly conversion_rate?: number | null;
}

export interface PaisAgregado {
  readonly country: string;
  readonly sessions: number;
  readonly revenue: number;
  readonly conversion_rate: number | null;
}

export const PAIS_DESCONHECIDO = "País Desconhecido";

const PAIS_NAO_ATRIBUIDO = "(not set)";

/**
 * Soma sessões e receita por país, pondera a taxa de conversão pelas sessões e devolve os
 * `limite` de maior volume. Desempata pelo nome, pelo mesmo motivo de `agruparPorNavegador`.
 */
export function agruparPorPais(
  linhas: readonly LinhaDeGeografia[],
  limite = 10,
): PaisAgregado[] {
  const porPais = new Map<
    string,
    {
      country: string;
      sessions: number;
      revenue: number;
      sessoesConvertidas: number;
      sessoesComTaxa: number;
    }
  >();

  for (const linha of linhas) {
    const country =
      !linha.country || linha.country === PAIS_NAO_ATRIBUIDO
        ? PAIS_DESCONHECIDO
        : linha.country;
    const atual = porPais.get(country) ?? {
      country,
      sessions: 0,
      revenue: 0,
      sessoesConvertidas: 0,
      sessoesComTaxa: 0,
    };
    const sessoes = linha.sessions ?? 0;
    atual.sessions += sessoes;
    atual.revenue += linha.revenue ?? 0;
    if (linha.conversion_rate != null) {
      atual.sessoesConvertidas += linha.conversion_rate * sessoes;
      atual.sessoesComTaxa += sessoes;
    }
    porPais.set(country, atual);
  }

  return [...porPais.values()]
    .sort(
      (a, b) => b.sessions - a.sessions || a.country.localeCompare(b.country),
    )
    .slice(0, limite)
    .map(
      ({ country, sessions, revenue, sessoesConvertidas, sessoesComTaxa }) => ({
        country,
        sessions,
        revenue,
        conversion_rate:
          sessoesComTaxa > 0 ? sessoesConvertidas / sessoesComTaxa : null,
      }),
    );
}
